import {ResponseBomType, SurveyPageType} from "../../../store/Models/Surveys/SurveyDataModel";
import {
    getMainSupplierFacingMaterialInventoryList,
    materalListSizeBatchAnswerThreshold,
    materiaListSizeGroupThreshold
} from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import {
    CoreSurveyResponseData,
    InventoryDetailsResponse
} from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels";
import {NavContext} from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates";

export class SurveyResponseProgressTextHelper {
    // TODO: these need to be localized

    static Instructions = "Instructions";
    static SurveyInfo = "Company-level Questionnaire";
    static GroupMaterials = "Material Groups";
    static InventoryDetails = "Product Overview";
    static Batch = "Bulk Inputs";
    static BOM = "Ingredient Disclosure Data";
    static DigitalProductPassport = "Digital Product Passport";
    static AddMaterials = "Add Materials"
    
    static mapPageTypeToText: { [key in SurveyPageType]?: any } = {
        [SurveyPageType.Instructions]: this.Instructions,
        [SurveyPageType.GroupInventories]: this.GroupMaterials,
        [SurveyPageType.Inventory]: this.InventoryDetails,
        [SurveyPageType.SupplierQuestionnaire]: this.SurveyInfo,
        [SurveyPageType.AddMaterials]: this.AddMaterials,
        [SurveyPageType.Bom]: this.BOM,
        [SurveyPageType.Batch]: this.Batch
    }

    static topLevelPagesOrder(coreData: CoreSurveyResponseData): SurveyPageType[] {
        const order = [SurveyPageType.Instructions];

        const hasSupplierLevelQuestionnaire = (coreData.response.supplierQuestionnaireData?.questionnaire ?? null) !== null;
        const isOpenBom = coreData?.response?.bomType === ResponseBomType.Open;
        
        const mainSupplierFacingMaterialInventoryList = getMainSupplierFacingMaterialInventoryList();

        if (hasSupplierLevelQuestionnaire) {
            order.push(SurveyPageType.SupplierQuestionnaire);
        }
        
        if(isOpenBom) {
            order.push(SurveyPageType.AddMaterials)
        }

        if (mainSupplierFacingMaterialInventoryList.length >= materalListSizeBatchAnswerThreshold) {
            order.push(SurveyPageType.Batch);
        }

        if (mainSupplierFacingMaterialInventoryList.length >= materiaListSizeGroupThreshold && (coreData.totalInventories > 1)) {
            order.push(SurveyPageType.GroupInventories);
        }

        if (mainSupplierFacingMaterialInventoryList.length > 0) {
            order.push(SurveyPageType.Inventory);
        }

        return order;
    }

    static getInventoryFirstSubpageText(inventory: InventoryDetailsResponse, language: string): string | undefined {
        if (inventory.hasBomPage) {
            return this.BOM;
        } else if (inventory.questionnaireList !== null && inventory.questionnaireList.length > 0 && inventory.questionnaireList[0].pages?.length > 0) {
            return inventory.questionnaireList[0].pages[0].languageToTitleMap[language] ?? inventory.questionnaireList[0].pages[0].title;
        }

        return undefined;
    }

    static getCurrentInventoryStep(inventory: InventoryDetailsResponse | null, navContext: NavContext): number {
        let steps = 0;

        if (inventory !== null) {
            if(navContext.mainNavIndex === SurveyPageType.Inventory && navContext.surveyQuestionnaireIndex < 0 && navContext.surveyQuestionnairePageIndex < 0)
                return steps;
            
            if (navContext.mainNavIndex === SurveyPageType.Bom) {
                steps++;
                return steps;
            } else {
                if (inventory.hasBomPage) {
                    steps++;
                }

                if (inventory.questionnaireList !== null && inventory.questionnaireList.length > 0) {
                    for (var i = 0; i < inventory.questionnaireList.length; i++) {
                        if (i < navContext.surveyQuestionnaireIndex) {
                            steps = steps + inventory.questionnaireList[i].pages.length;
                        } else {
                            steps = steps + navContext.surveyQuestionnairePageIndex + 1;

                            return steps;
                        }
                    }
                }
            }
        }

        return steps;
    }

    static getMaxNumberOfInventorySteps(inventory: InventoryDetailsResponse | null): number {
        let steps = 0;

        if (inventory !== null) {
            if (inventory.hasBomPage) {
                steps++;
            }

            if (inventory.questionnaireList !== null && inventory.questionnaireList.length > 0) {
                for (var i = 0; i < inventory.questionnaireList.length; i++) {
                    if (inventory.questionnaireList[i].pages?.length > 0) {
                        steps = steps + inventory.questionnaireList[i].pages.length;
                    }
                }
            }
        }

        return steps;
    }

    static getNextTextByNavigationContext(inventory: InventoryDetailsResponse | null, navContext: NavContext, coreData: CoreSurveyResponseData, language: string): string | undefined {
        if (navContext.mainNavIndex === SurveyPageType.Bom && inventory !== null) {
            if (inventory.questionnaireList !== null && inventory.questionnaireList.length > 0) {
                return inventory.questionnaireList[0].pages[0].languageToTitleMap[language] ?? inventory.questionnaireList[0].pages[0]?.title;
            }
        } else if (navContext.mainNavIndex === SurveyPageType.Inventory && inventory !== null) {
            if (navContext.surveyQuestionnaireIndex === -1) {
                if (inventory.hasBomPage) {
                    return this.mapPageTypeToText[SurveyPageType.Bom];
                } else if (inventory.questionnaireList !== null && inventory.questionnaireList.length > 0) {
                    return inventory.questionnaireList[0].pages[0]?.languageToTitleMap[language] ?? inventory.questionnaireList[0].pages[0]?.title;
                }
            } else if (navContext.surveyQuestionnairePageIndex !== -1) {
                if (navContext.surveyQuestionnairePageIndex + 1 < inventory.questionnaireList[navContext.surveyQuestionnaireIndex]?.pages?.length) {
                    return inventory.questionnaireList[navContext.surveyQuestionnaireIndex].pages[navContext.surveyQuestionnairePageIndex + 1].languageToTitleMap[language] ?? inventory.questionnaireList[navContext.surveyQuestionnaireIndex].pages[navContext.surveyQuestionnairePageIndex + 1].title;
                } else {
                    if (navContext.surveyQuestionnaireIndex + 1 < inventory.questionnaireList.length) {
                        if (inventory.questionnaireList[navContext.surveyQuestionnaireIndex + 1]?.pages.length > 0) {
                            return inventory.questionnaireList[navContext.surveyQuestionnaireIndex + 1].pages[0].languageToTitleMap[language] ?? inventory.questionnaireList[navContext.surveyQuestionnaireIndex + 1].pages[0].title;
                        }
                    }
                }
            }
        } else {
            const topLevelPages = this.topLevelPagesOrder(coreData);
            const currentIndex = topLevelPages.findIndex(i => i === navContext.mainNavIndex);

            if (currentIndex < topLevelPages.length + 1) {
                return this.mapPageTypeToText[topLevelPages[currentIndex + 1]];
            }
        }

        return undefined;
    }
}